/* eslint-disable import/no-cycle */
import {createRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {Form, Input, Switch} from 'antd';
import {CheckOutlined, CloseOutlined} from '@ant-design/icons';
import {datetimeFormat} from '../../config/moment';
import {transformation} from '../../utils/transformations';
import {ValidationRules} from '../../utils/validationRules';
import {FormItemLayouts} from '../shared/form/formItemLayouts';
import CancelSaveButtonGroup from '../shared/buttons/CancelSaveButtonGroup';
import Select from '../shared/inputs/Select';
import {sortAlphabetically} from '../shared/tables/sorters';
import {useStore} from '../../hooks/useStore';
import {useMount} from '../../hooks/useMount';
import AlertWarning from '../shared/alert/AlertWarning';

const UnclassifiedInterruptionsForm = ({
  record,
  fields,
  onChange,
  saving = false,
  onSubmit,
  onCancel,
  saveButtonDisabled,
  fieldsDisabled,
}) => {
  const store = useStore();
  const [form] = Form.useForm();
  const formRef = createRef();
  const {t} = useTranslation();
  const [sourceId, setSourceId] = useState(null);
  const [messageRequired, setMessageRequired] = useState(false);
  const [interruptionReasons, setInterruptionReasons] = useState([]);
  const [interruptionReasonSources, setInterruptionReasonSources] = useState([]);

  const initialValues = record
    ? {
      start: moment(record.start).format(datetimeFormat),
      durationSeconds: transformation.detailedDuration(record.durationSeconds),
      message: record.message,
    }
    : {};

  useMount(() => {
    const availableReasons = store.interruptionReasonStore.reasonsForSelectedWorkplace;

    const availableReasonSourceIds = new Set(availableReasons.map((reason) => reason.reasonSourceId));
    const availableReasonSources = store.interruptionReasonSourceStore.sources
      .filter((source) => availableReasonSourceIds.has(source.id))
      .sort((a, b) => sortAlphabetically(a.name, b.name));

    setInterruptionReasons(availableReasons);
    setInterruptionReasonSources(availableReasonSources);
  });

  return (
    <Form
      form={form}
      ref={formRef}
      {...FormItemLayouts.default}
      labelAlign={'left'}
      onFinish={onSubmit}
      fields={fields}
      initialValues={initialValues}
      onFieldsChange={(changedFields, allFields) => onChange && onChange(allFields)}
    >
      {
        record ? (
          <>
            <Form.Item name={'start'} label={t('operationStateLog.model.attributes.start')}>
              <Input disabled/>
            </Form.Item>
            <Form.Item name={'durationSeconds'} label={t('operationStateLog.model.attributes.durationSeconds')}>
              <Input disabled/>
            </Form.Item>
            <Form.Item name={'active'} label={t('operationStateLog.model.attributes.active')}>
              <Switch
                disabled
                checked={record.active}
                checkedChildren={<CheckOutlined/>}
                unCheckedChildren={<CloseOutlined/>}
              />
            </Form.Item>
          </>
        ) : ''
      }
      <Form.Item
        name={'interruptionReasonSourceId'}
        {...FormItemLayouts.fullWidth}
        label={t('interruptionReasonSource.model.one')}
      >
        {interruptionReasonSources.length === 0 && !store.interruptionReasonStore.isLoadingCollection && (
          <AlertWarning
            message={t('interruption.modal.form.reason.noReasonsConfigured')}
            closable
          />
        )}
        <Select
          disabled={fieldsDisabled}
          onSelect={(value) => {
            if (value !== sourceId) {
              formRef.current.setFieldsValue({interruptionReasonId: undefined});
              setMessageRequired(false);
              setSourceId(value);
            }
          }}
        >
          {
            interruptionReasonSources.map((source) => (
              <Select.Option key={source.id} value={source.id}>
                {source.name}
              </Select.Option>
            ))
          }
        </Select>
      </Form.Item>
      <Form.Item
        name={'interruptionReasonId'}
        {...FormItemLayouts.fullWidth}
        label={t('interruptionReason.model.one')}
        rules={[ValidationRules.required]}
      >
        <Select
          disabled={fieldsDisabled || sourceId === null}
          onSelect={(value) => {
            const reason = interruptionReasons.find((element) => element.id === value);
            setMessageRequired((reason) ? reason.isMessageRequired : false);
          }}
        >
          {
            interruptionReasons.map((reason) => {
              if (reason.reasonSourceId === sourceId) {
                return (
                  <Select.Option key={reason.id} value={reason.id}>
                    {reason.name}
                  </Select.Option>
                );
              }
              return null;
            })
          }
        </Select>
      </Form.Item>
      <Form.Item
        name={'message'}
        {...FormItemLayouts.fullWidth}
        label={t('operationStateLog.model.attributes.message')}
        rules={(messageRequired) ? [ValidationRules.required] : []}
      >
        <Input.TextArea disabled={fieldsDisabled}/>
      </Form.Item>
      <Form.Item {...FormItemLayouts.fullWidth} style={{textAlign: 'right'}}>
        <CancelSaveButtonGroup
          onCancel={onCancel}
          onSave={saving}
          saveButtonDisabled={fieldsDisabled || saveButtonDisabled}
        />
      </Form.Item>
    </Form>
  );
};

export default UnclassifiedInterruptionsForm;
